import { useRef } from 'react';
import { HeadingContainer, LinkContainer, SchematicContainer, ImageContainer } from './styles';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Typography, Box } from '@mui/material';
import { IconButton, Tooltip } from '@mui/material';
import SaveIcon from '@mui/icons-material/SaveAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { setNetlist } from '../../redux/modelDataSlice';
import Link from '../Link/Link';
import { APP_NAME, URL } from '../../utils/constants/general.js';
import api from '../../api';
import { recordEvent } from '../../utils/analytics/local';
import { ReactComponent as NetlistIcon } from '../../images/icons/netlistIcon.svg';
import { ReactComponent as SpiceModelIcon } from '../../images/icons/spiceModelIcon.svg';
import SchematicSVG from './SchematicSVG/SchematicSVG';
import { sendAdobeEvent } from '../../utils/analytics/adobe.js';

const Schematic = ({ simulationNumber, netlistPath, simulation, showInputParams }) => {
	const inputParams = useSelector(state => state.modelData.inputParams);
	const simulations = useSelector(state => state.designDefinition.partDefinition.simulations);
	const opnName = useSelector(state => state.designDefinition.partDefinition.mainHeading);
	const wpn = useSelector(state => state.designDefinition.partDefinition.wpn);
	const simulationName = simulations[simulationNumber].heading.replace(/ /g, '_');
	const dispatch = useDispatch();

	const addNetlistUnit = (acc, { value, mappingName, netlistUnit }) => {
		if (netlistUnit) {
			acc[mappingName] = value + netlistUnit;
		} else {
			acc[mappingName] = value;
		}
		return acc;
	};

	const transformWrapperRef = useRef();

	const zoomIn = () => {
		transformWrapperRef.current.zoomIn();
	};

	const zoomOut = () => {
		transformWrapperRef.current.zoomOut();
	};

	const sanitizeSVG = svgString => {
		const notAllowedAttributes = ['data-value', 'data-formatted-string', 'data-mapping-name', 'data-unit'];
		// Regular expression to match not allowed attributes
		const notAllowedAttributesRegex = new RegExp(`\\s*(${notAllowedAttributes.join('|')})="[^"]*"`, 'g');
		const sanitizedSVGString = svgString.replace(notAllowedAttributesRegex, '');
		const parser = new DOMParser();
		const doc = parser.parseFromString(sanitizedSVGString, 'image/svg+xml');
		return new XMLSerializer().serializeToString(doc);
	};

	const downloadSchematic = simulationNumber => {
		const svgElement = document
			.querySelector('.schematic_container' + simulationNumber)
			.shadowRoot.querySelector('#schematic_el');
		const sanitizedSVG = sanitizeSVG(svgElement.outerHTML);

		const blob = new Blob([sanitizedSVG], {
			type: 'image/svg+xml;charset=utf-8'
		});
		saveAs(blob, 'SpiceLiveModel_' + opnName + ' - ' + simulationName + '_schematic.svg');
	};

	const prepareSimulationData = () => {
		return Object.values(inputParams[simulationNumber]).reduce(addNetlistUnit, {});
	};

	const downloadData = (data, fileName) => {
		const blob = new Blob([data], { type: 'text/plain' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.download = fileName;
		link.href = url;
		link.click();
		window.URL.revokeObjectURL(url);
	};

	const handleDownloadNetlist = async netlist => {
		try {
			downloadData(netlist, `${simulationName}_${opnName}_netlist.txt`);
		} catch (err) {
			console.error(err);
		}
	};

	const handleDownloadRawData = async rawData => {
		try {
			downloadData(rawData, `${simulationName}_${opnName}_raw_data.txt`);
		} catch (err) {
			console.error(err);
		}
	};

	const handleGetNetlist = async () => {
		const inputData = prepareSimulationData();
		try {
			const netlist = await api.getNetlist({
				parameters: inputData,
				netlistPath: netlistPath
			});
			await handleDownloadNetlist(netlist);
			dispatch(setNetlist({ simulationNumber, netlist }));
		} catch (err) {
			console.error(err);
		}
	};

	const handleGetRawData = async () => {
		const inputData = prepareSimulationData();
		try {
			const rawData = await api.getRawData({
				parameters: inputData,
				netlistPath: netlistPath
			});
			await handleDownloadRawData(rawData);
		} catch (err) {
			console.error(err);
		}
	};

	const handleEventRecord = (link, event) => {
		recordEvent(link.heading.replace(/ /g, '_'), JSON.stringify(opnName));
		sendAdobeEvent(event.spaType, event.spaUrl, event.spaTitle, event.assetLink, event.assetTitle, event.wpn);
	};

	return (
		<SchematicContainer>
			<HeadingContainer>
				{simulation.schematic && (
					<Typography variant="subtitle2" sx={{ mb: 2 }}>
						{simulation.schematic.heading}:
					</Typography>
				)}
				{simulation.simulationLinks &&
					simulation.simulationLinks.map((links, index) => {
						const linkUrl = links.link || URL.TECHNICAL_SUPPORT_CENTER;
						return (
							<LinkContainer key={`link-container-${index}`}>
								<Link
									href={linkUrl}
									target="_blank"
									onClick={() => {
										handleEventRecord(links, {
											spaType: 'Model',
											spaUrl: window.location.href,
											spaTitle: APP_NAME + ' | ' + opnName,
											assetLink: linkUrl,
											assetTitle: wpn + '_Model',
											wpn: wpn
										});
									}}>
									<SpiceModelIcon alt="simulationModelLinkIcon" height={32} width={32} />
								</Link>
								<Link
									href={linkUrl}
									target="_blank"
									onClick={() => {
										handleEventRecord(links, {
											spaType: 'Model',
											spaUrl: window.location.href,
											spaTitle: APP_NAME + ' | ' + opnName,
											assetLink: linkUrl,
											assetTitle: wpn + '_Model',
											wpn: wpn
										});
									}}>
									<Typography variant="subtitle2" component="h6" sx={{ ml: 1.5 }}>
										{links.heading}
									</Typography>
								</Link>
							</LinkContainer>
						);
					})}

				{simulation.netlist && simulation.netlist.displayNetlist && (
					<LinkContainer>
						<Link
							target="_blank"
							onClick={() => {
								handleGetNetlist();
								handleEventRecord(simulation.netlist, {
									spaType: 'Netlist',
									spaUrl: window.location.href,
									spaTitle: APP_NAME + ' | ' + opnName,
									assetLink: null,
									assetTitle: simulation.schematic.heading + ' ' + opnName,
									wpn: wpn
								});
							}}>
							<NetlistIcon alt="simulationModelLinkIcon" height={32} width={32} />
						</Link>
						<Link
							target="_blank"
							onClick={() => {
								handleGetNetlist();
								handleEventRecord(simulation.netlist, {
									spaType: 'Netlist',
									spaUrl: window.location.href,
									spaTitle: APP_NAME + ' | ' + opnName,
									assetLink: null,
									assetTitle: simulation.schematic.heading + ' ' + opnName,
									wpn: wpn
								});
							}}>
							<Typography variant="subtitle2" component="h6" sx={{ ml: 1.5 }}>
								{simulation.netlist.heading}
							</Typography>
						</Link>
					</LinkContainer>
				)}
				{simulation.showRawData && (
					<Link target="_blank" onClick={handleGetRawData}>
						<Typography variant="subtitle2" component="h6" sx={{ ml: 1.5 }}>
							Raw Data
						</Typography>
					</Link>
				)}
				<Box>
					<Typography variant="body1" component="span" sx={{ mr: 1.5 }}>
						Hold Ctrl to zoom
					</Typography>
					<Tooltip title="Zoom In">
						<IconButton aria-label="Zoom In" onClick={zoomIn} sx={{ m: '0 5px' }}>
							<ZoomInIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Zoom Out">
						<IconButton aria-label="Zoom Out" onClick={zoomOut} sx={{ m: '0 5px' }}>
							<ZoomOutIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Download SVG">
						<IconButton
							aria-label="Download SVG"
							onClick={() => {
								downloadSchematic(simulationNumber);
							}}
							sx={{ m: '0 5px' }}>
							<SaveIcon />
						</IconButton>
					</Tooltip>
				</Box>
			</HeadingContainer>

			{simulation.schematic && (
				<TransformWrapper ref={transformWrapperRef} disabled={false} wheel={{ activationKeys: ['Control', 'Shift'] }}>
					<TransformComponent>
						<ImageContainer showInputParams={showInputParams}>
							<SchematicSVG schematicPath={simulation.schematic.schematicPath} simulationNumber={simulationNumber} />
						</ImageContainer>
					</TransformComponent>
				</TransformWrapper>
			)}
		</SchematicContainer>
	);
};

export default Schematic;
